import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield,
  AlertTriangle,
  BarChart3,
  Search,
  Activity,
  Globe,
  Target,
  FileText,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  TrendingUp,
  Users,
  ExternalLink,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const CHART_COLORS = ['#10b981', '#059669', '#047857', '#03634a', '#065f46', '#064e3b', '#374151', '#6b7280'];

interface GTDStatistics {
  total_incidents: number;
  date_range: { min_year?: number; max_year?: number };
  countries_count: number;
  groups_count: number;
  attack_types: Record<string, number>;
  target_types: Record<string, number>;
  weapon_types: Record<string, number>;
  casualties: {
    total_killed?: number;
    total_wounded?: number;
    total_us_killed?: number;
    total_us_wounded?: number;
  };
}

interface GTDIncident {
  eventid?: string;
  iyear?: number;
  country_txt?: string;
  region_txt?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  attacktype1_txt?: string;
  targtype1_txt?: string;
  weaptype1_txt?: string;
  gname?: string;
  nkill?: number;
  nwound?: number;
  summary?: string;
}

interface GTDAlert {
  incident_id?: string;
  severity: string;
  title: string;
  description?: string;
  country?: string;
  city?: string;
  attack_type?: string;
  target_type?: string;
  group_name?: string;
  casualties?: { killed?: number; wounded?: number };
  detected_at: string;
}

type SortField = 'iyear' | 'country_txt' | 'city' | 'attacktype1_txt' | 'targtype1_txt' | 'weaptype1_txt' | 'gname' | 'nkill' | 'nwound';
type SortDir = 'asc' | 'desc';

const CyberTerrorism: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'query' | 'analytics' | 'alerts' | 'groups'>('overview');
  const [stats, setStats] = useState<GTDStatistics | null>(null);
  const [timelineData, setTimelineData] = useState<{ year: string; incidents: number; killed: number; wounded: number }[]>([]);
  const [incidents, setIncidents] = useState<GTDIncident[]>([]);
  const [incidentsTotal, setIncidentsTotal] = useState(0);
  const [alerts, setAlerts] = useState<GTDAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Query filters
  const [queryFilters, setQueryFilters] = useState({
    start_year: '',
    end_year: '',
    countries: '',
    regions: '',
    attack_types: [] as string[],
    target_types: [] as string[],
    weapon_types: [] as string[],
    min_fatalities: '',
    limit: 100,
  });

  // Table sort
  const [sortField, setSortField] = useState<SortField>('iyear');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Analytics
  const [analyticsGroupBy, setAnalyticsGroupBy] = useState<string>('year');
  const [analyticsData, setAnalyticsData] = useState<Record<string, any>>({});
  const [analyticsFilters, setAnalyticsFilters] = useState({ start_year: '', end_year: '' });

  // SATP Terrorist Groups
  const [satpGroups, setSatpGroups] = useState<any[]>([]);
  const [satpTotal, setSatpTotal] = useState(0);
  const [satpSearch, setSatpSearch] = useState('');
  const [satpCountryFilter, setSatpCountryFilter] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);

  useEffect(() => {
    loadStatistics();
  }, []);

  useEffect(() => {
    if (stats?.total_incidents) {
      loadTimeline();
    }
  }, [stats?.total_incidents]);

  useEffect(() => {
    if (activeTab === 'alerts') loadAlerts();
    if (activeTab === 'analytics') loadAnalytics();
    if (activeTab === 'groups') loadSATPGroups();
    if (activeTab === 'query') loadIncidents();
  }, [activeTab]);

  const loadStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (await apiClient.getCyberTerrorismStatistics()) as GTDStatistics;
      setStats(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load statistics. GTD data may not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async () => {
    try {
      const result = (await apiClient.getCyberTerrorismAnalytics({ group_by: 'year' })) as any;
      const byYear = result?.analytics?.by_year || {};
      const arr = Object.entries(byYear)
        .map(([year, v]: [string, any]) => ({
          year: String(year),
          incidents: v?.count ?? 0,
          killed: v?.total_killed ?? 0,
          wounded: v?.total_wounded ?? 0,
        }))
        .sort((a, b) => parseInt(a.year) - parseInt(b.year));
      setTimelineData(arr);
    } catch {
      setTimelineData([]);
    }
  };

  const loadIncidents = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { limit: queryFilters.limit, offset: 0 };
      if (queryFilters.start_year) params.start_year = parseInt(queryFilters.start_year);
      if (queryFilters.end_year) params.end_year = parseInt(queryFilters.end_year);
      if (queryFilters.countries.trim())
        params.countries = queryFilters.countries.split(',').map((c) => c.trim()).filter(Boolean);
      if (queryFilters.regions.trim())
        params.regions = queryFilters.regions.split(',').map((r) => r.trim()).filter(Boolean);
      if (queryFilters.attack_types.length) params.attack_types = queryFilters.attack_types;
      if (queryFilters.target_types.length) params.target_types = queryFilters.target_types;
      if (queryFilters.weapon_types.length) params.weapon_types = queryFilters.weapon_types;
      if (queryFilters.min_fatalities) params.min_fatalities = parseInt(queryFilters.min_fatalities);
      const result = (await apiClient.queryCyberTerrorismIncidents(params)) as any;
      setIncidents(result?.incidents || []);
      setIncidentsTotal(result?.total || 0);
    } catch (e: any) {
      setError(e.message || 'Failed to query incidents.');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    if (!analyticsGroupBy) return;
    setLoading(true);
    setError(null);
    try {
      const params: any = { group_by: analyticsGroupBy };
      if (analyticsFilters.start_year) params.start_year = parseInt(analyticsFilters.start_year);
      if (analyticsFilters.end_year) params.end_year = parseInt(analyticsFilters.end_year);
      const result = (await apiClient.getCyberTerrorismAnalytics(params)) as any;
      setAnalyticsData(result?.analytics || {});
    } catch (e: any) {
      setError(e.message || 'Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  };

  const loadGTDData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await apiClient.loadCyberTerrorismData()) as { records_loaded: number; message: string };
      await loadStatistics();
      alert(result.message || `Loaded ${result.records_loaded} records`);
    } catch (e: any) {
      setError(e.message || 'Failed to load GTD data.');
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await apiClient.getCyberTerrorismAlerts(50)) as any;
      setAlerts(result?.alerts || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load alerts.');
    } finally {
      setLoading(false);
    }
  };

  const loadSATPGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await apiClient.getSATPTerroristGroups({
        search: satpSearch || undefined,
        countries: satpCountryFilter || undefined,
        limit: 500,
        offset: 0,
      })) as any;
      setSatpGroups(result?.groups || []);
      setSatpTotal(result?.total || 0);
    } catch (e: any) {
      setError(e.message || 'Failed to load terrorist groups.');
    } finally {
      setLoading(false);
    }
  };

  const scrapeSATPGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = (await apiClient.scrapeSATPTerroristGroups()) as any;
      await loadSATPGroups();
      alert(result.message || `Scraped ${result.groups_stored} terrorist groups from SATP`);
    } catch (e: any) {
      setError(e.message || 'Failed to scrape SATP data.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedIncidents = useMemo(() => {
    const copy = [...incidents];
    copy.sort((a, b) => {
      let av: any = a[sortField];
      let bv: any = b[sortField];
      if (av == null) av = sortField.startsWith('n') ? 0 : '';
      if (bv == null) bv = sortField.startsWith('n') ? 0 : '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [incidents, sortField, sortDir]);

  const severityColor = (s: string) => {
    switch (s?.toLowerCase()) {
      case 'critical':
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      default:
        return 'secondary';
    }
  };

  // Chart data from stats
  const attackChartData = useMemo(() => {
    if (!stats?.attack_types) return [];
    return Object.entries(stats.attack_types)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([name, count]) => ({ name: name.length > 25 ? name.slice(0, 22) + '…' : name, count, fullName: name }));
  }, [stats?.attack_types]);

  const weaponChartData = useMemo(() => {
    if (!stats?.weapon_types) return [];
    return Object.entries(stats.weapon_types)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name: name.length > 20 ? name.slice(0, 17) + '…' : name, count, fullName: name }));
  }, [stats?.weapon_types]);

  const targetChartData = useMemo(() => {
    if (!stats?.target_types) return [];
    return Object.entries(stats.target_types)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name: name.length > 20 ? name.slice(0, 17) + '…' : name, count, fullName: name }));
  }, [stats?.target_types]);

  // Analytics chart data
  const analyticsChartData = useMemo(() => {
    const key = analyticsGroupBy === 'year' ? 'by_year' : analyticsGroupBy === 'country' ? 'by_country' : analyticsGroupBy === 'region' ? 'by_region' : analyticsGroupBy === 'attack_type' ? 'by_attack_type' : analyticsGroupBy === 'target_type' ? 'by_target_type' : '';
    const raw = analyticsData[key];
    if (!raw || typeof raw !== 'object') return [];
    return Object.entries(raw).map(([k, v]: [string, any]) => ({
      name: k?.length > 30 ? k.slice(0, 27) + '…' : k || 'Unknown',
      incidents: v?.count ?? 0,
      killed: v?.total_killed ?? 0,
      wounded: v?.total_wounded ?? 0,
      fullName: k,
    }));
  }, [analyticsData, analyticsGroupBy]);

  const toggleFilter = (type: 'attack' | 'target' | 'weapon', value: string) => {
    const keys = { attack: 'attack_types', target: 'target_types', weapon: 'weapon_types' } as const;
    const key = keys[type];
    const arr = queryFilters[key];
    const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
    setQueryFilters({ ...queryFilters, [key]: next });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-8 h-8 text-amber-600" />
          Cyber Terrorism Intelligence
        </h1>
        <p className="text-muted-foreground mt-1">
          GTD data analytics – trends, attack patterns, weapon & target analysis, and actionable intelligence
        </p>
      </div>

      {error && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="pt-4">
            <p className="text-amber-700 dark:text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </p>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="query" className="flex items-center gap-2">
            <Search className="w-4 h-4" />
            Query & Filter
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Terrorist Groups
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Alerts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={loadStatistics} disabled={loading} title="Refresh stats from database">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {user?.role === 'admin' && (
              <Button variant="default" size="sm" onClick={loadGTDData} disabled={loading} title="Import GTD from Excel file into database. Use only when adding new data.">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Import from Excel
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
              <p>Loading intelligence...</p>
            </div>
          ) : stats ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Total Incidents</CardTitle>
                    <FileText className="w-4 h-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.total_incidents?.toLocaleString() ?? 0}</div>
                    {stats.date_range && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {stats.date_range.min_year} – {stats.date_range.max_year}
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Countries</CardTitle>
                    <Globe className="w-4 h-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.countries_count ?? 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Groups Tracked</CardTitle>
                    <Target className="w-4 h-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.groups_count ?? 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Casualties</CardTitle>
                    <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {((stats.casualties?.total_killed ?? 0) + (stats.casualties?.total_wounded ?? 0)).toLocaleString()}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Killed: {stats.casualties?.total_killed?.toLocaleString() ?? 0} • Wounded: {stats.casualties?.total_wounded?.toLocaleString() ?? 0}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Timeline: Incidents by Year */}
              {timelineData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                      Incidents Over Time
                    </CardTitle>
                    <CardDescription>Annual trend of terrorist incidents from GTD database</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timelineData}>
                          <defs>
                            <linearGradient id="colorIncidents" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip
                            content={({ active, payload }) =>
                              active && payload?.length ? (
                                <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                                  <div className="font-semibold">{payload[0].payload.year}</div>
                                  <div>Incidents: {payload[0].payload.incidents?.toLocaleString()}</div>
                                  <div>Killed: {payload[0].payload.killed?.toLocaleString()}</div>
                                  <div>Wounded: {payload[0].payload.wounded?.toLocaleString()}</div>
                                </div>
                              ) : null
                            }
                          />
                          <Area type="monotone" dataKey="incidents" stroke="#10b981" fillOpacity={1} fill="url(#colorIncidents)" strokeWidth={2} name="Incidents" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Attack Types Bar Chart */}
              {attackChartData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Attack Patterns</CardTitle>
                    <CardDescription>Distribution by attack type (top 12)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[320px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={attackChartData} layout="vertical" margin={{ left: 8, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} />
                          <Tooltip
                            content={({ active, payload }) =>
                              active && payload?.[0] ? (
                                <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                                  <div className="font-semibold">{payload[0].payload.fullName}</div>
                                  <div>Incidents: {payload[0].payload.count?.toLocaleString()}</div>
                                </div>
                              ) : null
                            }
                          />
                          <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} name="Incidents" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Weapon & Target Types: Side by side */}
              <div className="grid md:grid-cols-2 gap-4">
                {weaponChartData.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Weapon Types</CardTitle>
                      <CardDescription>Distribution by weapon used</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[260px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={weaponChartData}>
                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip
                              content={({ active, payload }) =>
                                active && payload?.[0] ? (
                                  <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                                    <div className="font-semibold">{payload[0].payload.fullName}</div>
                                    <div>Incidents: {payload[0].payload.count?.toLocaleString()}</div>
                                  </div>
                                ) : null
                              }
                            />
                            <Bar dataKey="count" fill="#059669" radius={[4, 4, 0, 0]} name="Incidents" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {targetChartData.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Target Types</CardTitle>
                      <CardDescription>Distribution by target category</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[260px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={targetChartData}
                              dataKey="count"
                              nameKey="fullName"
                              cx="50%"
                              cy="50%"
                              outerRadius={90}
                              label={({ name }) => name}
                            >
                              {targetChartData.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              content={({ active, payload }) =>
                                active && payload?.[0] ? (
                                  <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                                    <div className="font-semibold">{payload[0].payload.fullName}</div>
                                    <div>Incidents: {payload[0].payload.count?.toLocaleString()}</div>
                                  </div>
                                ) : null
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground space-y-4">
                <p>No GTD data in database.</p>
                <p className="text-sm">
                  Stats load automatically from the database. To add data: place{' '}
                  <code className="bg-muted px-1 rounded">globalterrorismdb*.xlsx</code> in{' '}
                  <code className="bg-muted px-1 rounded">data/xlsx/</code>, then:
                </p>
                <ul className="text-sm text-left max-w-md mx-auto list-disc">
                  <li>As admin: click <strong>Import from Excel</strong> above, or</li>
                  <li>Run: <code className="bg-muted px-1 rounded">python scripts/load_gtd_data.py</code></li>
                </ul>
                <p className="text-xs">
                  Download GTD from{' '}
                  <a href="https://www.start.umd.edu/gtd/" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                    start.umd.edu/gtd
                  </a>
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="query" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Query & Filter Incidents
              </CardTitle>
              <CardDescription>Filter GTD incidents by year, country, region, attack type, weapon type, target type, and casualties</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <Label>Start Year</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 2010"
                    value={queryFilters.start_year}
                    onChange={(e) => setQueryFilters({ ...queryFilters, start_year: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Year</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 2023"
                    value={queryFilters.end_year}
                    onChange={(e) => setQueryFilters({ ...queryFilters, end_year: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Countries (comma-separated)</Label>
                  <Input
                    placeholder="Iraq, Afghanistan, Pakistan"
                    value={queryFilters.countries}
                    onChange={(e) => setQueryFilters({ ...queryFilters, countries: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Regions (comma-separated)</Label>
                  <Input
                    placeholder="Middle East & North Africa"
                    value={queryFilters.regions}
                    onChange={(e) => setQueryFilters({ ...queryFilters, regions: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Min Fatalities</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={queryFilters.min_fatalities}
                    onChange={(e) => setQueryFilters({ ...queryFilters, min_fatalities: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Limit</Label>
                  <Input
                    type="number"
                    value={queryFilters.limit}
                    onChange={(e) => setQueryFilters({ ...queryFilters, limit: parseInt(e.target.value) || 100 })}
                  />
                </div>
              </div>

              {stats && (
                <div className="space-y-3">
                  <Label>Filter by Attack Type</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(stats.attack_types)
                      .sort((a, b) => (stats.attack_types[b] ?? 0) - (stats.attack_types[a] ?? 0))
                      .slice(0, 15)
                      .map((name) => (
                        <div key={name} className="flex items-center gap-1.5">
                          <Checkbox
                            id={`at-${name}`}
                            checked={queryFilters.attack_types.includes(name)}
                            onCheckedChange={() => toggleFilter('attack', name)}
                          />
                          <label htmlFor={`at-${name}`} className="text-sm cursor-pointer">
                            {name} ({stats.attack_types[name]})
                          </label>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              {stats && (
                <div className="space-y-3">
                  <Label>Filter by Weapon Type</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(stats.weapon_types)
                      .sort((a, b) => (stats.weapon_types[b] ?? 0) - (stats.weapon_types[a] ?? 0))
                      .slice(0, 12)
                      .map((name) => (
                        <div key={name} className="flex items-center gap-1.5">
                          <Checkbox
                            id={`wt-${name}`}
                            checked={queryFilters.weapon_types.includes(name)}
                            onCheckedChange={() => toggleFilter('weapon', name)}
                          />
                          <label htmlFor={`wt-${name}`} className="text-sm cursor-pointer">
                            {name} ({stats.weapon_types[name]})
                          </label>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              {stats && (
                <div className="space-y-3">
                  <Label>Filter by Target Type</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(stats.target_types)
                      .sort((a, b) => (stats.target_types[b] ?? 0) - (stats.target_types[a] ?? 0))
                      .slice(0, 12)
                      .map((name) => (
                        <div key={name} className="flex items-center gap-1.5">
                          <Checkbox
                            id={`tt-${name}`}
                            checked={queryFilters.target_types.includes(name)}
                            onCheckedChange={() => toggleFilter('target', name)}
                          />
                          <label htmlFor={`tt-${name}`} className="text-sm cursor-pointer">
                            {name} ({stats.target_types[name]})
                          </label>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <Button onClick={loadIncidents} disabled={loading}>
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Query
              </Button>
            </CardContent>
          </Card>

          {!loading && incidents.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {stats?.total_incidents ? (
                  <p>No incidents match your filters. Try adjusting year range, countries, or other filters.</p>
                ) : (
                  <p>No GTD data in database. Import from Excel in the Overview tab first.</p>
                )}
              </CardContent>
            </Card>
          )}
          {incidents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Results ({incidentsTotal.toLocaleString()} total)</CardTitle>
                <CardDescription>Click column headers to sort</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background z-10">
                      <tr className="border-b">
                        {[
                          ['iyear', 'Year'],
                          ['country_txt', 'Country'],
                          ['city', 'City'],
                          ['attacktype1_txt', 'Attack'],
                          ['targtype1_txt', 'Target'],
                          ['weaptype1_txt', 'Weapon'],
                          ['gname', 'Group'],
                          ['nkill', 'Killed'],
                          ['nwound', 'Wounded'],
                        ].map(([field, label]) => (
                          <th
                            key={field}
                            className="text-left p-2 cursor-pointer hover:bg-muted/50 select-none"
                            onClick={() => toggleSort(field as SortField)}
                          >
                            <span className="flex items-center gap-1">
                              {label}
                              {sortField === field ? (
                                sortDir === 'asc' ? (
                                  <ArrowUp className="w-3 h-3" />
                                ) : (
                                  <ArrowDown className="w-3 h-3" />
                                )
                              ) : (
                                <ArrowUpDown className="w-3 h-3 opacity-50" />
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedIncidents.map((inc, i) => (
                        <tr key={inc.eventid || i} className="border-b hover:bg-muted/30">
                          <td className="p-2">{inc.iyear ?? '—'}</td>
                          <td className="p-2">{inc.country_txt ?? '—'}</td>
                          <td className="p-2">{inc.city ?? '—'}</td>
                          <td className="p-2">{inc.attacktype1_txt ?? '—'}</td>
                          <td className="p-2">{inc.targtype1_txt ?? '—'}</td>
                          <td className="p-2">{inc.weaptype1_txt ?? '—'}</td>
                          <td className="p-2">{inc.gname ?? '—'}</td>
                          <td className="p-2">{inc.nkill ?? 0}</td>
                          <td className="p-2">{inc.nwound ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Analytics</CardTitle>
              <CardDescription>Explore trends and patterns by dimension with interactive charts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label>Group By</Label>
                  <Select value={analyticsGroupBy} onValueChange={setAnalyticsGroupBy}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="year">Year (Timeline)</SelectItem>
                      <SelectItem value="country">Country</SelectItem>
                      <SelectItem value="region">Region</SelectItem>
                      <SelectItem value="attack_type">Attack Type</SelectItem>
                      <SelectItem value="target_type">Target Type</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start Year (optional)</Label>
                  <Input
                    type="number"
                    placeholder={stats?.date_range?.min_year ? `e.g. ${stats.date_range.min_year}` : 'e.g. 1970'}
                    className="w-28"
                    value={analyticsFilters.start_year}
                    onChange={(e) => setAnalyticsFilters({ ...analyticsFilters, start_year: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Year (optional)</Label>
                  <Input
                    type="number"
                    placeholder={stats?.date_range?.max_year ? `e.g. ${stats.date_range.max_year}` : 'e.g. 2023'}
                    className="w-28"
                    value={analyticsFilters.end_year}
                    onChange={(e) => setAnalyticsFilters({ ...analyticsFilters, end_year: e.target.value })}
                  />
                </div>
                <Button onClick={loadAnalytics} disabled={loading}>
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
                  Run Analytics
                </Button>
              </div>

              {analyticsChartData.length === 0 && !loading && (
                <div className=" py-8 text-center text-muted-foreground">
                  <p>
                    No data in selected range.
                    {stats?.date_range?.min_year != null && stats?.date_range?.max_year != null && (
                      <span className="block mt-1 text-sm">
                        Your GTD data covers {stats.date_range.min_year}–{stats.date_range.max_year}. Try adjusting the year range or load full GTD data for more years.
                      </span>
                    )}
                    {(!stats?.date_range?.min_year || !stats?.date_range?.max_year) && (
                      <span className="block mt-1 text-sm">Import GTD from Excel first, or clear year filters to see all data.</span>
                    )}
                  </p>
                </div>
              )}
              {analyticsChartData.length > 0 && (
                <div className="mt-6">
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      {analyticsGroupBy === 'year' ? (
                        <LineChart data={analyticsChartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip
                            content={({ active, payload }) =>
                              active && payload?.[0] ? (
                                <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                                  <div className="font-semibold">{payload[0].payload.fullName}</div>
                                  <div>Incidents: {payload[0].payload.incidents?.toLocaleString()}</div>
                                  <div>Killed: {payload[0].payload.killed?.toLocaleString()}</div>
                                  <div>Wounded: {payload[0].payload.wounded?.toLocaleString()}</div>
                                </div>
                              ) : null
                            }
                          />
                          <Legend />
                          <Line type="monotone" dataKey="incidents" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} name="Incidents" />
                          <Line type="monotone" dataKey="killed" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} name="Killed" />
                          <Line type="monotone" dataKey="wounded" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} name="Wounded" />
                        </LineChart>
                      ) : (
                        <BarChart data={analyticsChartData} layout="vertical" margin={{ left: 8, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 10 }} />
                          <Tooltip
                            content={({ active, payload }) =>
                              active && payload?.[0] ? (
                                <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
                                  <div className="font-semibold">{payload[0].payload.fullName}</div>
                                  <div>Incidents: {payload[0].payload.incidents?.toLocaleString()}</div>
                                  <div>Killed: {payload[0].payload.killed?.toLocaleString()}</div>
                                  <div>Wounded: {payload[0].payload.wounded?.toLocaleString()}</div>
                                </div>
                              ) : null
                            }
                          />
                          <Legend />
                          <Bar dataKey="incidents" fill="#10b981" radius={[0, 4, 4, 0]} name="Incidents" />
                          <Bar dataKey="killed" fill="#ef4444" radius={[0, 4, 4, 0]} name="Killed" />
                          <Bar dataKey="wounded" fill="#f59e0b" radius={[0, 4, 4, 0]} name="Wounded" />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="groups" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-500" />
                SATP Terrorist Groups
              </CardTitle>
              <CardDescription>
                South Asia Terrorism Portal – detailed profiles by country/region. Scrape to load data from{' '}
                <a href="https://www.satp.org/terrorist-groups/southasia" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                  SATP
                </a>
                .
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <Label>Search</Label>
                  <Input
                    placeholder="Group name, region..."
                    value={satpSearch}
                    onChange={(e) => setSatpSearch(e.target.value)}
                    className="w-48"
                  />
                </div>
                <div>
                  <Label>Country/Region</Label>
                  <Input
                    placeholder="e.g. India, Bangladesh"
                    value={satpCountryFilter}
                    onChange={(e) => setSatpCountryFilter(e.target.value)}
                    className="w-40"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={loadSATPGroups} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Search
                </Button>
                {user?.role === 'admin' && (
                  <Button size="sm" onClick={scrapeSATPGroups} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Scrape SATP
                  </Button>
                )}
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <Activity className="w-10 h-10 animate-spin mx-auto text-primary" />
                </div>
              ) : satpGroups.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  {satpTotal === 0 ? (
                    <>
                      <p>No terrorist group profiles loaded.</p>
                      <p className="text-sm mt-2">
                        {user?.role === 'admin'
                          ? 'Click "Scrape SATP" to fetch data from South Asia Terrorism Portal.'
                          : 'An admin can scrape SATP data to populate this section.'}
                      </p>
                    </>
                  ) : (
                    'No results for your filters.'
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 max-h-[480px] overflow-y-auto">
                    {satpGroups.map((g, i) => (
                      <Card
                        key={i}
                        className={`cursor-pointer hover:border-emerald-500/50 transition-colors ${selectedGroup === g ? 'border-emerald-500 ring-1 ring-emerald-500/30' : ''}`}
                        onClick={() => setSelectedGroup(g)}
                      >
                        <CardContent className="p-3">
                          <div className="font-medium text-sm">{g.name}</div>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {g.region && <Badge variant="outline">{g.region}</Badge>}
                            {g.country && g.country !== g.region && <Badge variant="secondary">{g.country}</Badge>}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <div className="border rounded-lg p-4 max-h-[480px] overflow-y-auto">
                    {selectedGroup ? (
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <h3 className="font-semibold text-lg">{selectedGroup.name}</h3>
                          {selectedGroup.source_url && (
                            <a
                              href={selectedGroup.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" /> Source
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {selectedGroup.region && <Badge variant="outline">{selectedGroup.region}</Badge>}
                          {selectedGroup.country && <Badge variant="secondary">{selectedGroup.country}</Badge>}
                        </div>
                        {selectedGroup.description && (
                          <div className="text-sm whitespace-pre-wrap text-muted-foreground">{selectedGroup.description.slice(0, 3000)}{selectedGroup.description.length > 3000 ? '...' : ''}</div>
                        )}
                        {selectedGroup.origin && (
                          <div>
                            <div className="font-medium text-xs text-muted-foreground uppercase">Origin</div>
                            <div className="text-sm mt-1">{selectedGroup.origin.slice(0, 800)}{selectedGroup.origin.length > 800 ? '...' : ''}</div>
                          </div>
                        )}
                        {selectedGroup.objectives && (
                          <div>
                            <div className="font-medium text-xs text-muted-foreground uppercase">Objectives</div>
                            <div className="text-sm mt-1">{selectedGroup.objectives.slice(0, 800)}{selectedGroup.objectives.length > 800 ? '...' : ''}</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-muted-foreground text-sm text-center py-12">Select a group to view details</div>
                    )}
                  </div>
                </div>
              )}
              {satpTotal > 0 && (
                <p className="text-xs text-muted-foreground">
                  Showing {satpGroups.length} of {satpTotal.toLocaleString()} groups
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={loadAlerts} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Alerts
            </Button>
          </div>
          {alerts.length === 0 && !loading ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No alerts yet. Create monitors and run checks to receive alerts.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert, i) => (
                <Card key={i}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={severityColor(alert.severity)}>{alert.severity}</Badge>
                          {alert.country && <Badge variant="outline">{alert.country}</Badge>}
                          {alert.city && <Badge variant="outline">{alert.city}</Badge>}
                        </div>
                        <h3 className="font-semibold mt-2">{alert.title}</h3>
                        {alert.description && <p className="text-sm text-muted-foreground mt-1">{alert.description}</p>}
                        {alert.attack_type && <p className="text-xs mt-1">Attack: {alert.attack_type}</p>}
                        {alert.group_name && <p className="text-xs">Group: {alert.group_name}</p>}
                        {alert.casualties && (
                          <p className="text-xs mt-1">
                            Killed: {alert.casualties.killed ?? 0} • Wounded: {alert.casualties.wounded ?? 0}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{new Date(alert.detected_at).toLocaleString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CyberTerrorism;
